import { useEffect, useState, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Popover from "@radix-ui/react-popover";
import { Cross2Icon } from "@radix-ui/react-icons";
import * as RadioGroup from "@radix-ui/react-radio-group";
import retirementContributionsIllustration from "../../../static/illustration--retirement-contributions.svg";
import CurrencyInput from "react-currency-input-field";
import { useAnswer } from "../../../util/form";
import { useAppStore } from "../../../store/store";

export default function Contributions({
  advanceScreen,
  previousScreen,
  updateValidation,
  value,
  screen,
}) {
  const { form } = useAppStore();

  const { answer, setAnswer } = useAnswer(screen.id);
  console.log('previous values', { value, answer });
  const answerUnit = useMemo(
    () => answer?.unit ?? "personalIntervalDollars",
    [answer],
  );
  const useDollars = useMemo(
    () => answerUnit === "personalIntervalDollars",
    [answerUnit],
  );

  const income = useMemo(() => {
    const incomePage = form.questions.find(
      (question) => question.questionId === "2.4",
    );
    return Number(incomePage?.answerKey) || 0;
  }, [form.questions]);

  // FIXME: values are not being saved going forward/backward
  const [goalsPersonalContribution, setGoalsPersonalContribution] = useState(
    value.goalsPersonalContribution || "",
  );
  const [goalsPersonalContributionError, setGoalsPersonalContributionError] =
    useState("");

  const [goalsEmployerContribution, setGoalsEmployerContribution] = useState(
    value.goalsEmployerContribution || "",
  );
  const [goalsEmployerContributionError, setGoalsEmployerContributionError] =
    useState("");

  const [goalsFrequency, setGoalsFrequency] = useState(
    value.goalsFrequency ? value.goalsFrequency : "Select",
  );

  const validateForm = () => {
    const isPersonalContributionValid =
      goalsPersonalContribution !== null &&
      goalsPersonalContribution !== "" &&
      goalsPersonalContribution !== undefined;
    const isEmployerContributionValid =
      goalsEmployerContribution !== null &&
      goalsEmployerContribution !== "" &&
      goalsEmployerContribution !== undefined;
    const isFrequencyValid = goalsFrequency !== "Select";
    const isPercentValid =
      answerUnit === "personalIntervalPercentage" &&
      goalsPersonalContribution <= 100 &&
      goalsEmployerContribution <= 100;

    if (
      answerUnit === "personalIntervalDollars" &&
      (isPersonalContributionValid || isEmployerContributionValid) &&
      isFrequencyValid
    ) {
      return true;
    } else if (
      answerUnit !== "personalIntervalDollars" &&
      isPersonalContributionValid &&
      isEmployerContributionValid &&
      isPercentValid &&
      isFrequencyValid
    ) {
      return true;
    } else {
      return false;
    }
  };

  const handleSetAnswerUnit = (unit) => {
    const answerValue = {
      unit: unit,
      goalsPersonalContribution: goalsPersonalContribution,
      goalsEmployerContribution: goalsEmployerContribution,
      goalsFrequency: goalsFrequency,
    };

    // In percentage mode the user's contributions are entered as an
    // annual percentage of salary (frequency is locked to "annually" above).
    // PortfolioInformation reads calculatedPersonal/EmployerContribution as
    // annual dollar amounts when unit is percentage; without these the raw
    // percent value gets treated as dollars and the "$10" of "10%" turns
    // into "$0.83/month".
    if (unit === "personalIntervalPercentage") {
      const personalPercent = Number(goalsPersonalContribution) || 0;
      const employerPercent = Number(goalsEmployerContribution) || 0;
      answerValue.calculatedPersonalContribution =
        (personalPercent / 100) * income;
      answerValue.calculatedEmployerContribution =
        (employerPercent / 100) * income;
    }

    setAnswer(answerValue);
  };

  useEffect(() => {
    console.log("useEffect being called with values:", {
      goalsPersonalContribution,
      goalsEmployerContribution,
      goalsFrequency,
      answerUnit,
    });
    // TODO: make a separate updateAnswer() and remove extraneous calls to handleSetAnswerUnit
    handleSetAnswerUnit(answerUnit);
    updateValidation(validateForm());

    setGoalsPersonalContributionError("");
    setGoalsEmployerContributionError("");

    if (answerUnit === "personalIntervalPercentage") {
      if (goalsPersonalContribution > 100) {
        setGoalsPersonalContributionError(
          "Personal contribution percentage cannot exceed 100%",
        );
      }
      if (goalsPersonalContribution < 0) {
        setGoalsPersonalContributionError(
          "Personal contribution percentage cannot be negative",
        );
      }
      if (goalsEmployerContribution > 100) {
        setGoalsEmployerContributionError(
          "Employer contribution percentage cannot exceed 100%",
        );
      }
      if (goalsEmployerContribution < 0) {
        setGoalsEmployerContributionError(
          "Employer contribution percentage cannot be negative",
        );
      }
    }

    if (answerUnit === "personalIntervalDollars") {
      if (goalsPersonalContribution > income) {
        setGoalsPersonalContributionError(
          "Personal contribution amount cannot exceed your income",
        );
      }
      if (goalsPersonalContribution < 0) {
        setGoalsPersonalContributionError(
          "Personal contribution amount cannot be negative",
        );
      }
      if (goalsEmployerContribution < 0) {
        setGoalsEmployerContributionError(
          "Employer contribution amount cannot be negative",
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalsPersonalContribution, goalsEmployerContribution, goalsFrequency]);

  // Reset values when switching units
  const resetInputs = useCallback(() => {
    setGoalsPersonalContribution("");
    setGoalsEmployerContribution("");
  }, []);

  return (
    <motion.div
      className="w-full h-full"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
    >
      <div className="px-6 py-16 text-center md:px-16 md:py-20">
        <div className="max-w-xl mx-auto">
          <img
            className="block w-20 h-auto mx-auto md:w-28"
            src={retirementContributionsIllustration}
            alt=""
          />
          <h1 className="mt-6 text-3xl md:mt-8 md:text-4xl">
            How much do you contribute to your retirement account?
          </h1>
          <div className="mt-3 mb-4 md:mt-5 md:mb-6">
            <span className="text-base font-bold text-gray-800 md:text-lg">
              Not sure?
            </span>
            <Popover.Root>
              <Popover.Trigger asChild>
                <button className="relative ml-[.25em] shadow-none before:absolute before:left-1/2 before:top-1/2 before:block before:h-11 before:w-11 before:-translate-x-1/2 before:-translate-y-1/2 before:content-['']">
                  <FontAwesomeIcon
                    icon="fa-regular fa-circle-info"
                    className="text-[.944em] text-as-green-100"
                  />
                </button>
              </Popover.Trigger>
              <Popover.Portal>
                <Popover.Content
                  sideOffset={5}
                  className="z-50 w-64 rounded bg-white p-5 shadow-xl will-change-[transform,opacity] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-as-green-50 data-[side=bottom]:animate-slide-up-fade data-[side=left]:animate-slide-right-fade data-[side=right]:animate-slide-left-fade data-[side=top]:animate-slide-down-fade"
                >
                  <div className="text-xs">
                    <p>
                      Your contribution rate and employer contribution are
                      usually listed on your pay stub or can be found on your
                      retirement plan&apos;s portal. If you&apos;re having
                      trouble locating it, don&apos;t worry, your HR or payroll
                      team can provide this information to you.
                    </p>
                  </div>
                  <Popover.Close
                    className="absolute right-1 top-1 inline-flex h-6 w-6 items-center justify-center rounded-full font-[inherit] text-gray-500 shadow-none hover:bg-gray-100 hover:text-gray-900"
                    aria-label="Close"
                  >
                    <Cross2Icon />
                  </Popover.Close>
                  <Popover.Arrow className="fill-white" />
                </Popover.Content>
              </Popover.Portal>
            </Popover.Root>
          </div>
          <div>
            <p>
              Please complete these fields for the most accurate recommendation.
              Your contributions help determine the projected growth of your
              account.
            </p>
          </div>
        </div>
        <div className="mx-auto h-auto max-w-[28.75rem] bg-white pt-12 md:flex md:flex-col md:pt-16">
          <form className="flex flex-col md:h-full">
            <fieldset>
              <div className="flex flex-col items-center md:flex-row">
                <legend className="mr-6 text-xs shrink-0">
                  Contribution Unit
                </legend>
                <RadioGroup.Root
                  className="flex space-x-3 grow"
                  aria-label="Contribution unit"
                  defaultValue={answerUnit}
                >
                  <div className="flex items-center w-full">
                    <RadioGroup.Item
                      className="relative w-full rounded-lg border border-as-green-100 bg-transparent pb-4 pl-14 pr-4 pt-4 text-left before:absolute before:left-4 before:top-[1.1875rem] before:block before:h-4 before:w-4 before:rounded-full before:border before:border-as-green-100 before:bg-transparent before:content-[''] data-[state=checked]:bg-as-green-0 data-[state=checked]:before:bg-as-green-100"
                      value="personalIntervalDollars"
                      id="personalIntervalDollars"
                      defaultChecked
                      onClick={() => {
                        handleSetAnswerUnit("personalIntervalDollars");
                        setGoalsFrequency("Select");
                        resetInputs();
                      }}
                    >
                      <RadioGroup.Indicator className="absolute left-[1.3125rem] top-6 h-1.5 w-1.5 rounded-full after:absolute after:inset-0 after:block after:h-1.5 after:w-1.5 after:rounded-full after:bg-white after:content-['']" />
                      <label
                        className="block leading-normal text-gray-800 cursor-pointer"
                        htmlFor="personalIntervalDollars"
                      >
                        $ US Dollar
                      </label>
                    </RadioGroup.Item>
                  </div>
                  <div className="flex items-center w-full">
                    <RadioGroup.Item
                      className="relative w-full rounded-lg border border-as-green-100 bg-transparent pb-4 pl-14 pr-4 pt-4 text-left before:absolute before:left-4 before:top-[1.1875rem] before:block before:h-4 before:w-4 before:rounded-full before:border before:border-as-green-100 before:bg-transparent before:content-[''] data-[state=checked]:bg-as-green-0 data-[state=checked]:before:bg-as-green-100"
                      value="personalIntervalPercentage"
                      id="personalIntervalPercentage"
                      onClick={() => {
                        handleSetAnswerUnit("personalIntervalPercentage");
                        setGoalsFrequency("annually");
                        resetInputs();
                      }}
                    >
                      <RadioGroup.Indicator className="absolute left-[1.3125rem] top-6 h-1.5 w-1.5 rounded-full after:absolute after:inset-0 after:block after:h-1.5 after:w-1.5 after:rounded-full after:bg-white after:content-['']" />
                      <label
                        className="block leading-normal text-gray-800 cursor-pointer"
                        htmlFor="personalIntervalPercentage"
                      >
                        % Percent
                      </label>
                    </RadioGroup.Item>
                  </div>
                </RadioGroup.Root>
              </div>
              <div className="flex flex-col mt-2.5 text-left gap-y-5">
                <div>
                  <label
                    className="block mb-2.5 text-xs"
                    htmlFor="GoalsPersonalContribution"
                  >
                    Personal contribution
                  </label>
                  <div className="flex rounded shadow-sm">
                    <span className="inline-flex items-center rounded-l border border-r-0 border-as-green-100 px-4 text-gray-600 w-12 text-center justify-center shrink-0">
                      {useDollars ? "$" : "%"}
                    </span>
                    <CurrencyInput
                      id="GoalsPersonalContribution"
                      name="GoalsPersonalContribution"
                      value={goalsPersonalContribution}
                      aria-label="Personal contribution amount"
                      className={`w-full px-5 py-[1.125rem] mb-0 text-base text-gray-500 border-0 rounded-r ring-1 ring-inset placeholder:text-gray-300 focus:ring-2 focus:ring-inset ${
                        goalsPersonalContributionError
                          ? "ring-red-500 focus:ring-red-500"
                          : "ring-as-green-100 focus:ring-as-green-50"
                      }`}
                      placeholder="0.00"
                      onValueChange={(value) => {
                        setGoalsPersonalContribution(value);
                      }}
                      decimalsLimit="2"
                      prefix={useDollars ? "$" : ""}
                      suffix={useDollars ? "" : "%"}
                    />
                  </div>
                </div>
                {goalsPersonalContributionError && (
                  <p className="mt-2 text-sm text-red-600 text-center">
                    {goalsPersonalContributionError}
                  </p>
                )}

                <div>
                  <label
                    className="block mb-2.5 text-xs"
                    htmlFor="GoalsEmployerContribution"
                  >
                    Employer contribution
                  </label>
                  <div className="flex rounded shadow-sm">
                    <span className="inline-flex items-center rounded-l border border-r-0 border-as-green-100 px-4 text-gray-600 w-12 text-center justify-center shrink-0">
                      {useDollars ? "$" : "%"}
                    </span>
                    <CurrencyInput
                      id="GoalsEmployerContribution"
                      name="GoalsEmployerContribution"
                      value={goalsEmployerContribution}
                      aria-label="Employer contribution amount"
                      className={`w-full px-5 py-[1.125rem] mb-0 text-base text-gray-500 border-0 rounded-r ring-1 ring-inset placeholder:text-gray-300 focus:ring-2 focus:ring-inset ${
                        goalsEmployerContributionError
                          ? "ring-red-500 focus:ring-red-500"
                          : "ring-as-green-100 focus:ring-as-green-50"
                      }`}
                      placeholder="0.00"
                      onValueChange={(value) => {
                        setGoalsEmployerContribution(value);
                      }}
                      decimalsLimit="2"
                      prefix={useDollars ? "$" : ""}
                      suffix={useDollars ? "" : "%"}
                    />
                  </div>
                </div>
                {goalsEmployerContributionError && (
                  <p className="mt-2 text-sm text-red-600 text-center">
                    {goalsEmployerContributionError}
                  </p>
                )}

                <div className="">
                  <label
                    className="block mb-2.5 text-xs"
                    htmlFor="GoalsFrequency"
                  >
                    I get paid or make my contributions:
                  </label>
                  <select
                    id="GoalsFrequency"
                    name="GoalsFrequency"
                    value={useDollars ? goalsFrequency : "annually"} // Display "annually" when using percentages
                    disabled={!useDollars} // Disable when using percentages
                    required
                    aria-label="Frequency of contribution"
                    className="w-full px-5 py-[1.125rem] mb-0 text-base text-gray-500 border-0 rounded shadow-sm ring-1 ring-inset ring-as-green-100 placeholder:text-gray-300 focus:ring-2 focus:ring-inset focus:ring-as-green-50"
                    onChange={(e) => setGoalsFrequency(e.target.value)}
                  >
                    <option disabled value="Select">
                      Select frequency of contribution
                    </option>
                    <option value="weekly">weekly</option>
                    <option value="bi-weekly">bi-weekly</option>
                    <option value="semi-monthly">semi-monthly</option>
                    <option value="monthly">monthly</option>
                    <option value="annually">annually</option>
                  </select>
                </div>
              </div>
            </fieldset>
            <div className="flex justify-center gap-3 mt-6 md:mt-8 md:gap-5">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={previousScreen}
              >
                Back
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={!validateForm()}
                onClick={advanceScreen}
              >
                Continue
              </button>
            </div>
          </form>
        </div>
      </div>
    </motion.div>
  );
}
